import type { TypedChatWithMembers } from "@nexus/types"
import type { PayloadAction } from "@reduxjs/toolkit"

import { readPlaygroundDisplaySettings } from "../chat-settings.defaults"
import type { PlaygroundChatState } from "../playgroundTypes"

/**
 * Set loading state for individual chat history
 */
export const setLoadingChatHistory = (
  state: PlaygroundChatState,
  action: PayloadAction<boolean>
) => {
  state.metadata.isLoadingChatHistory = action.payload
}

/**
 * Clear newly created chat ID
 */
export const clearNewlyCreatedChatId = (state: PlaygroundChatState) => {
  state.metadata.newlyCreatedChatId = null
}

/**
 * Set newly created chat ID (for URL navigation)
 */
export const setNewlyCreatedChatId = (
  state: PlaygroundChatState,
  action: PayloadAction<{ chatId: string }>
) => {
  state.metadata.newlyCreatedChatId = action.payload.chatId

  if (!state.metadata.chatIds.includes(action.payload.chatId)) {
    state.metadata.chatIds.unshift(action.payload.chatId)
  }
}

/**
 * Handle loadPlaygroundChats.pending
 */
export const handleLoadChatsPending = (state: PlaygroundChatState) => {
  state.metadata.isLoadingChatList = true
  state.metadata.chatListError = null
}

/**
 * Handle loadPlaygroundChats.fulfilled
 */
export const handleLoadChatsFulfilled = (
  state: PlaygroundChatState,
  action: PayloadAction<{
    chats: TypedChatWithMembers[]
    hasNextPage: boolean
    page: number
  }>
) => {
  const { chats, hasNextPage, page } = action.payload

  state.metadata.isLoadingChatList = false
  state.metadata.hasNextPage = hasNextPage
  state.metadata.currentPage = page

  if (page > 1) {
    // Paginated load — append new chat IDs, skip duplicates
    const existingIds = new Set(state.metadata.chatIds)
    const newIds = chats.map((chat) => chat.id).filter((id) => !existingIds.has(id))
    state.metadata.chatIds.push(...newIds)
  } else if (page >= 1) {
    // First page load — replace entire list
    state.metadata.chatIds = chats.map((chat) => chat.id)
  } else {
    // page === 0 — individual chat load from URL
    const firstChat = chats[0]
    if (firstChat && !state.metadata.chatIds.includes(firstChat.id)) {
      state.metadata.chatIds.unshift(firstChat.id)
    }
  }

  for (const chat of chats) {
    const existingChat = state.chats[chat.id]

    if (existingChat) {
      // Update existing chat - preserve references to prevent re-renders
      existingChat.isNew = false
      existingChat.topic = chat.topic ?? null
      existingChat.metadata = chat.metadata ?? undefined
      existingChat.createdAt = new Date(chat.createdAt).getTime()
      existingChat.lastAccessedAt = new Date(chat.updatedAt || chat.createdAt).getTime()
      existingChat.currentAgentId = chat.agentId ?? undefined
      existingChat.agent = chat.agent ?? undefined
      const serverSettings = chat.settings ?? {}
      const persistedDisplay = readPlaygroundDisplaySettings()
      // The in-memory value wins over both the server payload and storage: a
      // chat-list refresh must not revert a toggle the user just flipped.
      existingChat.settings = {
        ...existingChat.settings,
        ...serverSettings,
        showToolCalls: existingChat.settings?.showToolCalls ?? persistedDisplay.showToolCalls,
        showThinking: existingChat.settings?.showThinking ?? persistedDisplay.showThinking
      }
      existingChat.lastMessage = chat.lastMessage
      existingChat.unread = chat.unread
      existingChat.numberOfPendingMessages = chat.numberOfPendingMessages
      existingChat.hasCodeInterpreterSession = chat.hasCodeInterpreterSession
    } else {
      // Create new chat instance
      state.chats[chat.id] = {
        id: chat.id,
        isNew: false,
        messages: [],
        isLoadingMessages: false,
        streamingMessages: {},
        isSendingMessage: false,
        shouldShowDynamicWhitespace: false,
        topic: chat.topic ?? null,
        metadata: chat.metadata ?? undefined,
        createdAt: new Date(chat.createdAt).getTime(),
        lastAccessedAt: new Date(chat.updatedAt || chat.createdAt).getTime(),
        unreadCount: 0,
        lastReadMessageId: undefined,
        currentAgentId: chat.agentId ?? undefined,
        agent: chat.agent ?? undefined,
        settings: {
          ...readPlaygroundDisplaySettings(),
          ...(chat.settings ?? {})
        },
        lastMessage: chat.lastMessage,
        unread: chat.unread,
        numberOfPendingMessages: chat.numberOfPendingMessages,
        hasMoreHistory: false,
        isLoadingMoreMessages: false,
        hasCodeInterpreterSession: chat.hasCodeInterpreterSession,
        messagesLoaded: false
      }
    }
  }
}

/**
 * Handle loadPlaygroundChats.rejected
 */
export const handleLoadChatsRejected = (
  state: PlaygroundChatState,
  action: { error: { message?: string } }
) => {
  state.metadata.isLoadingChatList = false
  state.metadata.chatListError = action.error.message || "Failed to load chat list"
}
